import React from 'react';
import { useNavigate } from 'react-router-dom';
import { IconSettings } from '@tabler/icons-react';
import { Box } from 'src/design/atoms/Box';
import { Tooltip } from 'src/design/atoms/Tooltip';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { JobSelector } from 'src/app/selectors/JobSelector';
import { FireIcon } from 'src/design/atoms/FireIcon';
import { useChatUi } from 'src/hooks/useChat';
import { useAuth } from 'src/hooks/useAuth';
import { useMenu } from 'src/hooks/useMenu';
import { MobileSection } from 'src/app/navigation/MobileSection';
import { Notifications } from 'src/app/navigation/Notifications';
import { Profile } from 'src/app/navigation/Profile';

/** @registry Nav row content right of the drawer toggle: job selector, settings/chatbot/notifications icons, and the profile menu (desktop) or MobileSection (mobile). */
export const HeaderContent: React.FC = () => {
  const { checkFeatureAllowed, user } = useAuth();
  const { isMobile } = useMenu();
  const navigate = useNavigate();
  const { openDrawer } = useChatUi();
  const hasChatbot = checkFeatureAllowed(['chatbot']);
  const hasSettings = checkFeatureAllowed([
    'settings_all',
    'settings_user',
    'settings_roles',
    'settings_offices',
    'settings_cost_codes',
    'settings_customers',
    'settings_contacts',
    'settings_rates',
    'settings_workflows',
    'settings_milestones',
    'settings_documents',
  ]);
  const hasJobs = checkFeatureAllowed(['jobs']);
  const settingsRoute = React.useMemo(() => {
    const hasUser = checkFeatureAllowed(['settings_all', 'settings_user']);
    if (hasUser) return '/settings/users';
    if (checkFeatureAllowed(['settings_roles'])) return '/settings/roles';
    if (checkFeatureAllowed(['settings_offices'])) return '/settings/offices';
    if (checkFeatureAllowed(['settings_customers']))
      return '/settings/customers';
    if (checkFeatureAllowed(['settings_contacts'])) return '/settings/contacts';
    if (checkFeatureAllowed(['settings_rates'])) return '/settings/rates';
    if (checkFeatureAllowed(['settings_workflows']))
      return '/settings/workflows';
    if (checkFeatureAllowed(['settings_milestones']))
      return '/settings/milestones';
    if (checkFeatureAllowed(['settings_documents']))
      return '/settings/documents';
    if (checkFeatureAllowed(['settings_cost_codes']))
      return '/settings/cost-codes';
    return '/settings/users';
  }, [user]);

  // On mobile, JobSelector lives in its own row below the navbar (rendered by
  // NavigationHeader). Here we render only the icons that belong in the nav row.
  return (
    <>
      {!isMobile && hasJobs && (
        <div style={{ width: '100%' }}>
          <div className="width-400">
            <JobSelector />
          </div>
        </div>
      )}
      {isMobile && <Box style={{ flex: 1 }} />}
      {hasSettings && !isMobile && (
        <BasicActionIcon
          variant="subtle"
          color="dark"
          size="lg"
          onClick={() => navigate(settingsRoute)}
          aria-label="Open settings"
        >
          <IconSettings />
        </BasicActionIcon>
      )}

      {hasChatbot && (
        <Tooltip label="F.I.R.E. — Firetrol Intelligent Response Engine">
          <BasicActionIcon
            variant="subtle"
            color="dark"
            size="lg"
            onClick={openDrawer}
            aria-label="Open F.I.R.E. assistant"
          >
            <FireIcon />
          </BasicActionIcon>
        </Tooltip>
      )}

      <Notifications />
      {!isMobile && <Profile />}
      {isMobile && (
        <MobileSection
          settingsRoute={hasSettings ? settingsRoute : undefined}
        />
      )}
    </>
  );
};
